import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Button,
  Card,
  ChevronDown,
  CirclePlay,
  EmptyState,
  Input,
  Layers,
  Modal,
  Pencil,
  Select,
  Upload,
  NoticeBox,
  Plus,
  SearchCheckList,
  SectionHeader,
  StatusDropdown,
  Tags,
  Trash2,
} from "../../ui";
import {
  createVideoSectionAPI,
  deleteVideoAPI,
  deleteVideoSectionAPI,
  fetchVideoSectionsAPI,
  fetchVideosAPI,
  reorderVideoSectionsAPI,
  updateVideoAPI,
  updateVideoSectionAPI,
  uploadVideoAPI,
} from "../../../utils/api/guidline";
import { DeleteModal } from "../../modals/Delete";
import { useAccess } from "../../../access/hooks/useAccess";
import { everyoneBut } from "../../../access/core/rule";
import { translations } from "../../../utils/lang";
import { APPS } from "../../ludDocuments/utils/ludDocuments.constants";
import { findGuideSection, findGuideVideo } from "../reportVideoGuide";

// Language labels are shown in their own language on purpose.
const VIDEO_LANGS = ["ar", "en"];
const VIDEO_LANG_LABELS = { ar: "العربية", en: "English" };

const videoLangLabel = (value) => VIDEO_LANG_LABELS[value] ?? value;

// "Uncategorized" is a virtual group: it has no database record.
const UNCATEGORIZED_KEY = "uncategorized";
const emptySectionForm = { id: "", nameAr: "", nameEn: "" };

const GUIDE_HIGHLIGHT_MS = 8000;
const GUIDE_MISSING_ID = "guideline-guide-missing";
const videoElementId = (id) => `guideline-video-${id}`;

// `guide` ({ app, report }) comes from the URL: that video is opened, scrolled to and highlighted.
export default function VideosGuideline({ lang, guide = null }) {
  const { can } = useAccess();
  const ar = lang === "ar";
  const canManage = can("guideline.video.manage");

  const [videos, setVideos] = useState([]);
  const [title, setTitle] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [newAccess, setNewAccess] = useState([]);
  const [sections, setSections] = useState([]);
  const [newSection, setNewSection] = useState("");
  const [collapsed, setCollapsed] = useState({});
  const [sectionForm, setSectionForm] = useState(null);
  const [sectionDeleteTarget, setSectionDeleteTarget] = useState(null);
  const [sectionBusy, setSectionBusy] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [videosLoaded, setVideosLoaded] = useState(false);
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const [guideFocus, setGuideFocus] = useState(null);
  const fileInputRef = useRef(null);

  // The list always shows the app language; switching it refetches.
  const contentLang = ar ? "ar" : "en";
  const contentLangRef = useRef(contentLang);
  contentLangRef.current = contentLang;

  // Options come from the project's role definitions, labelled in the app language.
  const roleLabels = translations[ar ? "ar" : "en"].Users.accesses;
  const accessOptions = useMemo(
    () => everyoneBut().map((role) => ({ value: role, label: roleLabels[role] || role })),
    [roleLabels],
  );
  const allRolesText = ar ? "كل الأدوار" : "All roles";
  const uncategorizedText = ar ? "غير مصنف" : "Uncategorized";

  const token = useMemo(() => localStorage.getItem("auth-token"), []);

  useEffect(() => {
    if (!token) return;

    // Ignore the response if the user leaves the section before it arrives.
    let cancelled = false;

    const getVideos = async () => {
      setFetching(true);
      const data = await fetchVideosAPI(token, contentLang);
      if (cancelled) return;
      setVideos(Array.isArray(data) ? data : []);
      setFetching(false);
      setVideosLoaded(true);
    };

    getVideos();

    return () => {
      cancelled = true;
    };
  }, [token, contentLang]);

  // Sections are language independent: both names are stored and the page language picks one.
  useEffect(() => {
    if (!token) return undefined;
    let cancelled = false;
    fetchVideoSectionsAPI(token).then((data) => {
      if (cancelled) return;
      setSections(Array.isArray(data) ? data : []);
      setSectionsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const sectionName = (section) =>
    (ar ? section.nameAr : section.nameEn) || section.nameAr || section.nameEn;

  const sectionOptions = useMemo(
    () => [
      { value: "", label: uncategorizedText },
      ...sections.map((section) => ({
        value: section._id,
        label: (ar ? section.nameAr : section.nameEn) || section.nameAr || section.nameEn,
      })),
    ],
    [sections, ar, uncategorizedText],
  );

  const guideReady = Boolean(guide) && videosLoaded && sectionsLoaded && !fetching;
  const guideVideo = useMemo(
    () => (guideReady ? findGuideVideo(videos, guide) : null),
    [guideReady, videos, guide],
  );
  const guideVideoId = guideVideo?._id;
  // No matching video: its Daily/Monthly section still opens, with the message inside it.
  const guideMissing = guideReady && !guideVideo;
  const guideSectionId = useMemo(
    () => (guideMissing ? findGuideSection(sections, guide)?._id : undefined),
    [guideMissing, sections, guide],
  );

  // A video whose section no longer exists is shown as Uncategorized too.
  const groups = useMemo(() => {
    const known = new Set(sections.map((section) => section._id));
    const loose = videos.filter((video) => !video.section || !known.has(video.section));
    const result = sections
      .map((section) => ({
        key: section._id,
        section,
        videos: videos.filter((video) => video.section === section._id),
      }))
      // Readers only see sections that hold something for them.
      .filter((group) => canManage || group.videos.length > 0 || group.key === guideSectionId);
    if (loose.length) result.push({ key: UNCATEGORIZED_KEY, section: null, videos: loose });
    return result;
  }, [sections, videos, canManage, guideSectionId]);

  const guideGroupKey = guideVideoId
    ? groups.find((group) => group.videos.some((video) => video._id === guideVideoId))?.key
    : guideSectionId;

  // Open the target's group, then scroll to it once it is rendered.
  useEffect(() => {
    if (!guideReady) return;
    if (guideGroupKey) setCollapsed((prev) => ({ ...prev, [guideGroupKey]: false }));
    setGuideFocus(guideVideoId ? videoElementId(guideVideoId) : GUIDE_MISSING_ID);
  }, [guideReady, guideGroupKey, guideVideoId]);

  useEffect(() => {
    if (!guideFocus) return undefined;
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    document.getElementById(guideFocus)?.scrollIntoView?.({
      behavior: reduceMotion ? "auto" : "smooth",
      block: "center",
    });
    const timer = setTimeout(() => setGuideFocus(null), GUIDE_HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [guideFocus]);

  const guideMissingNotice = guideMissing && (
    <div id={GUIDE_MISSING_ID} role="status" data-testid="guide-video-missing">
      <NoticeBox variant="info" size="sm">
        {ar
          ? `فيديو ${guide.report === "daily" ? "التقرير اليومي" : "التقرير الشهري"} لتطبيق ${APPS[guide.app]?.label?.ar || guide.app} غير متوفر حالياً.`
          : `The ${guide.report} report video for ${APPS[guide.app]?.label?.en || guide.app} is not available yet.`}
      </NoticeBox>
    </div>
  );

  const toggleGroup = (key) => setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));

  const closeSectionForm = () => {
    if (!sectionBusy) setSectionForm(null);
  };

  const handleSectionSave = async () => {
    if (!can("guideline.video.manage")) return;
    const nameAr = sectionForm.nameAr.trim();
    const nameEn = sectionForm.nameEn.trim();
    if (!nameAr || !nameEn || sectionBusy) return;

    setSectionBusy(true);
    const saved = sectionForm.id
      ? await updateVideoSectionAPI(token, sectionForm.id, { nameAr, nameEn })
      : await createVideoSectionAPI(token, { nameAr, nameEn });
    setSectionBusy(false);
    if (!saved) return;

    setSections((prev) =>
      sectionForm.id
        ? prev.map((section) => (section._id === saved._id ? saved : section))
        : [...prev, saved],
    );
    setSectionForm(null);
  };

  const handleSectionDelete = async (id) => {
    if (!can("guideline.video.manage")) return;
    if (!(await deleteVideoSectionAPI(token, id))) return;

    setSections((prev) => prev.filter((section) => section._id !== id));
    // Its videos fall back to Uncategorized (the server did the same).
    setVideos((prev) =>
      prev.map((video) => (video.section === id ? { ...video, section: null } : video)),
    );
    setSectionDeleteTarget(null);
  };

  const handleSectionMove = async (id, direction) => {
    if (!can("guideline.video.manage")) return;
    const index = sections.findIndex((section) => section._id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= sections.length || sectionBusy) return;

    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    setSectionBusy(true);
    const saved = await reorderVideoSectionsAPI(
      token,
      next.map((section) => section._id),
    );
    setSectionBusy(false);
    if (saved) setSections(saved);
  };

  const handleSectionChange = async (id, section) => {
    if (!can("guideline.video.manage")) return;
    if (savingId) return;

    setSavingId(id);
    const updated = await updateVideoAPI(token, id, { section: section || null });
    setSavingId(null);
    if (!updated) return;

    setVideos((prev) => prev.map((video) => (video._id === id ? { ...video, ...updated } : video)));
  };

  const handleUpload = async () => {
    // handler-level authorization (NA-010): the controls are only hidden for other roles
    if (!can("guideline.video.manage")) return;
    if (!videoFile || loading) return;

    setLoading(true);

    const uploaded = await uploadVideoAPI(token, {
      title: title.trim() || videoFile.name,
      video: videoFile,
      lang: contentLang,
      access: newAccess,
      section: newSection,
    });

    if (uploaded) {
      setVideos((prev) => [uploaded, ...prev]);
      setTitle("");
      setVideoFile(null);
      setNewAccess([]);
      setNewSection("");
      // Clear the native input too, so picking the same file again fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }

    setLoading(false);
  };

  const handleLangChange = async (id, newLang) => {
    if (!can("guideline.video.manage")) return;
    if (savingId) return;

    setSavingId(id);
    const updated = await updateVideoAPI(token, id, { lang: newLang });
    setSavingId(null);

    if (!updated) return;

    setVideos((prev) =>
      prev
        .map((video) => (video._id === id ? { ...video, ...updated } : video))
        // A video moved to the other language leaves the current list.
        .filter((video) => (video.lang || "ar") === contentLangRef.current),
    );
  };

  const handleAccessChange = async (id, access) => {
    if (!can("guideline.video.manage")) return;
    if (savingId) return;

    setSavingId(id);
    const updated = await updateVideoAPI(token, id, { access });
    setSavingId(null);

    if (!updated) return;

    setVideos((prev) =>
      prev.map((video) => (video._id === id ? { ...video, ...updated } : video)),
    );
  };

  const handleDelete = async (id) => {
    if (!can("guideline.video.manage")) return;
    const deleted = await deleteVideoAPI(token, id);

    if (deleted) {
      setVideos((prev) => prev.filter((video) => video._id !== id));
    }
  };

  const renderVideoCard = (video) => (
    <Card
      key={video._id}
      id={videoElementId(video._id)}
      variant="guideline"
      padding="lg"
      className={`guideline-accent primary guideline-video-card ${guideFocus === videoElementId(video._id) ? "is-guide-target" : ""}`}
    >
      <div className="ui-card-guideline-content">
        <div className="guideline-video-card-head">
          <div className="ui-card-head">
            <span className="ui-icon-box primary">
              <CirclePlay size={18} />
            </span>
            <span className="guideline-video-title">{video.title}</span>
            {canManage ? (
              <StatusDropdown
                className="guideline-video-lang"
                value={video.lang || "ar"}
                options={VIDEO_LANGS}
                getLabel={videoLangLabel}
                editable={savingId !== video._id}
                onChange={(next) => handleLangChange(video._id, next)}
              />
            ) : (
              <Tags variant="info" size="sm">
                {videoLangLabel(video.lang || "ar")}
              </Tags>
            )}
          </div>

          {canManage && (
            <Button
              type="button"
              variant="danger"
              appearance=""
              size="sm"
              title={ar ? "حذف الفيديو" : "Delete video"}
              aria-label={ar ? "حذف الفيديو" : "Delete video"}
              icon={<Trash2 size={15} />}
              onClick={() => handleDelete(video._id)}
            ></Button>
          )}
        </div>

        {canManage && (
          <Select
            className="guideline-video-section-select"
            label={ar ? "القسم" : "Section"}
            options={sectionOptions}
            value={sections.some((section) => section._id === video.section) ? video.section : ""}
            onChange={(e) => handleSectionChange(video._id, e.target.value)}
            placeholder={uncategorizedText}
            disabled={savingId === video._id}
          />
        )}

        {canManage && (
          <SearchCheckList
            fullWidth="guideline-video-access"
            label={ar ? "الأدوار المسموح لها بالمشاهدة" : "Roles allowed to view"}
            options={accessOptions}
            selected={video.access || []}
            onChange={(next) => handleAccessChange(video._id, next)}
            placeholder={allRolesText}
            disabled={savingId === video._id}
          />
        )}

        <video controls preload="metadata" className="guideline-video-player">
          <source src={video.videoUrl} />
          {ar
            ? "المتصفح لا يدعم تشغيل الفيديو."
            : "Your browser does not support the video tag."}
        </video>
      </div>
    </Card>
  );

  const renderGroup = (group) => {
    const isOpen = !collapsed[group.key];
    const sectionIndex = group.section
      ? sections.findIndex((item) => item._id === group.section._id)
      : -1;
    const bodyId = `guideline-section-${group.key}`;
    const groupTitle = group.section ? sectionName(group.section) : uncategorizedText;

    return (
      <section key={group.key} className="guideline-video-section" aria-label={groupTitle}>
        <div className="guideline-video-section-head">
          <button
            type="button"
            className="guideline-video-section-toggle"
            aria-expanded={isOpen}
            aria-controls={bodyId}
            onClick={() => toggleGroup(group.key)}
          >
            <ChevronDown
              size={18}
              className={`guideline-video-section-chevron ${isOpen ? "open" : ""}`}
              aria-hidden="true"
            />
            <span className="guideline-video-section-title">{groupTitle}</span>
            <Tags variant="info" size="sm">
              {group.videos.length}
            </Tags>
          </button>

          {canManage && group.section && (
            <div className="guideline-video-section-actions">
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                size="sm"
                title={ar ? "تحريك للأعلى" : "Move up"}
                aria-label={ar ? "تحريك للأعلى" : "Move up"}
                icon={<ArrowUp size={15} />}
                disabled={sectionBusy || sectionIndex === 0}
                onClick={() => handleSectionMove(group.section._id, -1)}
              ></Button>
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                size="sm"
                title={ar ? "تحريك للأسفل" : "Move down"}
                aria-label={ar ? "تحريك للأسفل" : "Move down"}
                icon={<ArrowDown size={15} />}
                disabled={sectionBusy || sectionIndex === sections.length - 1}
                onClick={() => handleSectionMove(group.section._id, 1)}
              ></Button>
              <Button
                type="button"
                variant="neutral"
                appearance="ghost"
                size="sm"
                title={ar ? "تعديل القسم" : "Edit section"}
                aria-label={ar ? "تعديل القسم" : "Edit section"}
                icon={<Pencil size={15} />}
                onClick={() =>
                  setSectionForm({
                    id: group.section._id,
                    nameAr: group.section.nameAr,
                    nameEn: group.section.nameEn,
                  })
                }
              ></Button>
              <Button
                type="button"
                variant="danger"
                appearance=""
                size="sm"
                title={ar ? "حذف القسم" : "Delete section"}
                aria-label={ar ? "حذف القسم" : "Delete section"}
                icon={<Trash2 size={15} />}
                onClick={() => setSectionDeleteTarget(group.section)}
              ></Button>
            </div>
          )}
        </div>

        {isOpen && (
          <div id={bodyId} className="guideline-video-section-body">
            {group.key === guideSectionId && guideMissingNotice}
            {group.videos.length === 0 && group.key === guideSectionId ? null : group.videos.length === 0 ? (
              <EmptyState
                compact
                icon={<Layers size={22} />}
                title={ar ? "لا توجد فيديوهات في هذا القسم" : "No videos in this section"}
              >
                {ar
                  ? "انقل فيديو إلى هذا القسم أو اختره عند رفع فيديو جديد."
                  : "Move a video here or pick this section when uploading a new one."}
              </EmptyState>
            ) : (
              <div className="guideline-videos-grid">{group.videos.map(renderVideoCard)}</div>
            )}
          </div>
        )}
      </section>
    );
  };

  return (
    <div
      className={`section ${ar ? "arabic-section" : "english-section"}`}
      id="videos"
    >
      <div className="ui-guideline-section">
        <SectionHeader
          icon={CirclePlay}
          title={ar ? "الفيديوهات التوضيحية" : "Tutorial Videos"}
          description={
            ar
              ? "شاهد الشروحات المرئية للمنصة وكيفية استخدامها"
              : "Watch visual tutorials for the platform and how to use it"
          }
          variant="primary"
        />

        {canManage && (
          <Card
            variant="guideline"
            padding="lg"
            className="guideline-accent primary guideline-video-upload-card"
          >
            <div className="ui-card-guideline-content">
              <div className="ui-card-head">
                <span className="ui-icon-box primary">
                  <Plus size={18} />
                </span>
                {ar ? "إضافة فيديو جديد" : "Add new video"}
              </div>

              <div className="guideline-video-upload-form">
                <Input
                  type="text"
                  placeholder={ar ? "اسم الفيديو..." : "Video title..."}
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />

                <label className="guideline-upload-btn">
                  <Input
                    ref={fileInputRef}
                    type="file"
                    accept="video/mp4,video/webm,video/quicktime"
                    className="guideline-upload-input"
                    onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
                  />

                  <Upload size={18} aria-hidden="true" />

                  <span className="guideline-upload-btn-text">
                    {videoFile
                      ? videoFile.name
                      : ar
                        ? "اختيار فيديو"
                        : "Choose video"}
                  </span>
                </label>

                <Select
                  className="guideline-video-section-select"
                  label={ar ? "القسم (اختياري)" : "Section (optional)"}
                  options={sectionOptions}
                  value={newSection}
                  onChange={(e) => setNewSection(e.target.value)}
                  placeholder={uncategorizedText}
                />

                <SearchCheckList
                  fullWidth="guideline-video-access"
                  label={ar ? "الأدوار المسموح لها بالمشاهدة" : "Roles allowed to view"}
                  options={accessOptions}
                  selected={newAccess}
                  onChange={setNewAccess}
                  placeholder={allRolesText}
                />

                <Button
                  type="button"
                  variant="primary"
                  size="sm"
                  onClick={handleUpload}
                  disabled={!videoFile || loading}
                >
                  {loading
                    ? ar
                      ? "جاري الرفع..."
                      : "Uploading..."
                    : ar
                      ? "رفع الفيديو"
                      : "Upload video"}
                </Button>
              </div>
            </div>
          </Card>
        )}

        {canManage && (
          <div className="guideline-section-toolbar">
            <Button
              type="button"
              variant="primary"
              appearance="soft"
              size="sm"
              icon={<Plus size={16} />}
              onClick={() => setSectionForm({ ...emptySectionForm })}
            >
              {ar ? "إضافة قسم" : "Add section"}
            </Button>
          </div>
        )}

        {!guideSectionId && guideMissingNotice}

        {fetching ? (
          <p className="ui-requirement-description" role="status">
            {ar ? "جاري تحميل الفيديوهات..." : "Loading videos..."}
          </p>
        ) : groups.length === 0 ? (
          <p className="ui-requirement-description">
            {ar ? "لا توجد فيديوهات مضافة حالياً." : "No videos added yet."}
          </p>
        ) : (
          groups.map(renderGroup)
        )}

        {sectionForm && (
          <Modal
            size="sm"
            busy={sectionBusy}
            title={
              sectionForm.id
                ? ar
                  ? "تعديل القسم"
                  : "Edit section"
                : ar
                  ? "إضافة قسم"
                  : "Add section"
            }
            closeLabel={ar ? "إغلاق" : "Close"}
            onClose={closeSectionForm}
            footer={
              <>
                <Button
                  type="button"
                  variant="neutral"
                  appearance="outline"
                  disabled={sectionBusy}
                  onClick={closeSectionForm}
                >
                  {ar ? "إلغاء" : "Cancel"}
                </Button>
                <Button
                  type="button"
                  variant="primary"
                  loading={sectionBusy}
                  disabled={!sectionForm.nameAr.trim() || !sectionForm.nameEn.trim() || sectionBusy}
                  onClick={handleSectionSave}
                >
                  {ar ? "حفظ" : "Save"}
                </Button>
              </>
            }
          >
            <div className="guideline-section-form">
              <Input
                label="الاسم بالعربية"
                value={sectionForm.nameAr}
                dir="rtl"
                onChange={(e) => setSectionForm((prev) => ({ ...prev, nameAr: e.target.value }))}
                autoFocus
              />
              <Input
                label="Name in English"
                value={sectionForm.nameEn}
                dir="ltr"
                onChange={(e) => setSectionForm((prev) => ({ ...prev, nameEn: e.target.value }))}
              />
            </div>
          </Modal>
        )}

        {sectionDeleteTarget && (
          <DeleteModal
            id={sectionDeleteTarget._id}
            description={
              ar
                ? `سيتم حذف القسم "${sectionName(sectionDeleteTarget)}" ونقل فيديوهاته إلى "غير مصنف". لن يتم حذف أي فيديو.`
                : `The section "${sectionName(sectionDeleteTarget)}" will be deleted and its videos moved to Uncategorized. No video is deleted.`
            }
            handleDelete={handleSectionDelete}
            cancel={() => setSectionDeleteTarget(null)}
          />
        )}
      </div>
    </div>
  );
}
